import { useEffect, useMemo, useState } from "react";
import {
    Alert,
    AutoComplete,
    Card,
    DatePicker,
    Empty,
    Flex,
    Input,
    Skeleton,
    Typography,
} from "antd";
import { Chart } from "@highcharts/react";
import type { Options, SeriesOptionsType, XAxisPlotBandsOptions, YAxisOptions } from "highcharts";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";

import { CustomResult } from "@repo/ui";
import { useDynamicOfSalesChartArea } from "@src/queries";
import type {
    DynamicOfSalesChartAreaRow,
    DynamicOfSalesGranularity,
    DynamicOfSalesGranularityAlias,
} from "@src/types";

const { RangePicker } = DatePicker;
const { Text, Title } = Typography;

const DEFAULT_CHART_HEIGHT = 440;
const INPUT_DEBOUNCE_MS = 350;

type ChartMetricKey =
    | "order_count"
    | "order_entry_total_price_sum"
    | "order_entry_min_price"
    | "profit"
    | "product_place"
    | "product_rating"
    | "product_min_price"
    | "product_delivery_duration";

type AxisId = "default" | "money" | "place" | "rating";

interface MetricConfig {
    axis: AxisId;
    color: string;
    label: string;
    type: SeriesOptionsType["type"];
    valueType: "currency" | "decimal" | "duration" | "number";
}

const GRANULARITY_OPTIONS: Array<{ value: DynamicOfSalesGranularityAlias; label: string }> = [
    { value: "quarter_month", label: "Четверть месяца" },
    { value: "half_month", label: "Половина месяца" },
    { value: "month", label: "Месяц" },
];

const GRANULARITY_INPUT_OPTIONS = [
    ...GRANULARITY_OPTIONS.map((option) => ({
        value: option.value,
        label: `${option.value} (${option.label})`,
    })),
    ...["1", "2", "7", "10"].map((value) => ({
        value,
        label: value,
    })),
];

const METRIC_CONFIG: Record<ChartMetricKey, MetricConfig> = {
    order_count: {
        axis: "default",
        color: "#2563eb",
        label: "Количество заказов",
        type: "column",
        valueType: "number",
    },
    order_entry_total_price_sum: {
        axis: "money",
        color: "#0f766e",
        label: "Сумма продаж",
        type: "line",
        valueType: "currency",
    },
    order_entry_min_price: {
        axis: "money",
        color: "#14b8a6",
        label: "Мин. цена заказа",
        type: "line",
        valueType: "currency",
    },
    profit: {
        axis: "money",
        color: "#16a34a",
        label: "Прибыль",
        type: "line",
        valueType: "currency",
    },
    product_place: {
        axis: "place",
        color: "#f97316",
        label: "Позиция товара",
        type: "line",
        valueType: "number",
    },
    product_rating: {
        axis: "rating",
        color: "#8b5cf6",
        label: "Рейтинг товара",
        type: "line",
        valueType: "decimal",
    },
    product_min_price: {
        axis: "money",
        color: "#e11d48",
        label: "Мин. цена товара",
        type: "line",
        valueType: "currency",
    },
    product_delivery_duration: {
        axis: "default",
        color: "#475569",
        label: "Срок доставки",
        type: "line",
        valueType: "duration",
    },
};

const compactNumberFormatter = new Intl.NumberFormat("ru-RU");
const currencyFormatter = new Intl.NumberFormat("ru-RU");
const decimalFormatter = new Intl.NumberFormat("ru-RU", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
});

function parseNumberLike(value: unknown): number | null {
    if (typeof value === "number") {
        return Number.isFinite(value) ? value : null;
    }

    if (typeof value !== "string") return null;

    const trimmed = value.trim();
    if (!trimmed) return null;

    const normalized = trimmed.replace(",", ".");
    if (/^[+-]?\d*\.?\d+$/.test(normalized)) {
        const parsed = Number.parseFloat(normalized);
        return Number.isFinite(parsed) ? parsed : null;
    }

    const matches = normalized.match(/[+-]?\d+(?:\.\d+)?/g);
    if (!matches?.length) return null;

    const numbers = matches
        .map((item) => Number.parseFloat(item))
        .filter((item) => Number.isFinite(item));

    if (!numbers.length) return null;
    if (numbers.length === 1) return numbers[0] ?? null;

    return numbers.reduce((sum, item) => sum + item, 0) / numbers.length;
}

function formatMetricValue(value: number | null | undefined, metric: ChartMetricKey): string {
    if (value === null || value === undefined || !Number.isFinite(value)) {
        return "—";
    }

    const config = METRIC_CONFIG[metric];

    if (config.valueType === "currency") {
        return `${currencyFormatter.format(value)} ₸`;
    }

    if (config.valueType === "decimal") {
        return decimalFormatter.format(value);
    }

    if (config.valueType === "duration") {
        return `${decimalFormatter.format(value)} дн.`;
    }

    return compactNumberFormatter.format(value);
}

function formatBucketLabel(row: DynamicOfSalesChartAreaRow): string {
    const start = dayjs(row.bucket_date_from);
    const end = dayjs(row.bucket_date_to);

    if (start.isSame(end, "day")) {
        return start.format("DD MMM YYYY");
    }

    return `${start.format("DD MMM")} - ${end.format("DD MMM YYYY")}`;
}

function buildPlotBands(rows: DynamicOfSalesChartAreaRow[]): XAxisPlotBandsOptions[] {
    return rows.flatMap((row, index) => {
        const from = index - 0.48;
        const to = index + 0.48;
        const bands: XAxisPlotBandsOptions[] = [];

        if (row.product_out_of_stock) {
            bands.push({
                from,
                to,
                color: "rgba(239, 68, 68, 0.14)",
            });
        }

        if ((row.product_discount ?? 0) > 0) {
            bands.push({
                from,
                to,
                color: "rgba(250, 204, 21, 0.18)",
                label: {
                    text: `Скидка ${row.product_discount}`,
                    align: "center",
                    y: 14,
                    style: {
                        color: "#854d0e",
                        fontSize: "10px",
                        fontWeight: "600",
                    },
                },
            });
        }

        return bands;
    });
}

function isGranularityAlias(value: string): value is DynamicOfSalesGranularityAlias {
    return GRANULARITY_OPTIONS.some((option) => option.value === value);
}

function isGranularityInputValid(value: string): boolean {
    const normalized = value.trim().toLowerCase();
    if (!normalized) return true;
    return isGranularityAlias(normalized) || /^\d+$/.test(normalized);
}

function normalizeGranularityInput(value: string): DynamicOfSalesGranularity {
    const normalized = value.trim().toLowerCase();

    if (!normalized) return "half_month";
    if (isGranularityAlias(normalized)) return normalized;
    if (/^\d+$/.test(normalized)) {
        return Number.parseInt(normalized, 10);
    }

    return "half_month";
}

function getSeriesAxisIndex(axis: AxisId): number {
    if (axis === "money") return 1;
    if (axis === "place") return 2;
    if (axis === "rating") return 3;
    return 0;
}

export interface DynamicOfSalesChartProps {
    merchantProductId: string;
}

export function DynamicOfSalesChart({ merchantProductId }: DynamicOfSalesChartProps) {
    const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>(() => {
        const end = dayjs().endOf("day");
        return [end.subtract(3, "month").startOf("month"), end];
    });
    const [granularityInput, setGranularityInput] = useState("half_month");
    const [cityIdInput, setCityIdInput] = useState("");
    const [debouncedCityId, setDebouncedCityId] = useState("");

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            setDebouncedCityId(cityIdInput.trim());
        }, INPUT_DEBOUNCE_MS);

        return () => window.clearTimeout(timeoutId);
    }, [cityIdInput]);

    const [dateFrom, dateTo] = useMemo(() => {
        const [start, end] = dateRange;
        return [start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD")];
    }, [dateRange]);

    const isGranularityInvalid = useMemo(
        () => !isGranularityInputValid(granularityInput),
        [granularityInput],
    );

    const normalizedGranularity = useMemo(
        () => normalizeGranularityInput(granularityInput),
        [granularityInput],
    );

    const chartQuery = useDynamicOfSalesChartArea({
        merchantProductId,
        cityId: debouncedCityId || null,
        dateFrom,
        dateTo,
        granularity: normalizedGranularity,
        enabled: Boolean(merchantProductId) && !isGranularityInvalid,
    });

    const chartData = chartQuery.data;
    const rows = chartData?.rows ?? [];

    const categories = useMemo(() => rows.map((row) => formatBucketLabel(row)), [rows]);

    const seriesMetrics = useMemo(() => {
        const keys = Object.keys(METRIC_CONFIG) as ChartMetricKey[];
        return keys.filter((metric) => {
            return rows.some((row) => {
                const value = row[metric];
                return parseNumberLike(value) !== null;
            });
        });
    }, [rows]);

    const outOfStockCount = useMemo(
        () => rows.filter((row) => row.product_out_of_stock).length,
        [rows],
    );

    const discountCount = useMemo(
        () => rows.filter((row) => (row.product_discount ?? 0) > 0).length,
        [rows],
    );

    const chartOptions = useMemo<Options | undefined>(() => {
        if (!rows.length || !seriesMetrics.length) return undefined;

        const placeValues = rows
            .map((row) => parseNumberLike(row.product_place))
            .filter((value): value is number => value !== null);
        const ratingValues = rows
            .map((row) => parseNumberLike(row.product_rating))
            .filter((value): value is number => value !== null);

        const placeAxisMin =
            placeValues.length > 0 ? Math.max(0, Math.floor(Math.min(...placeValues) - 1)) : 0;
        const placeAxisMax = placeValues.length > 0 ? Math.ceil(Math.max(...placeValues) + 1) : 10;

        const ratingAxisMin =
            ratingValues.length > 0 ? Math.max(0, Math.min(...ratingValues) - 0.35) : 0;
        const ratingAxisMax = ratingValues.length > 0 ? Math.max(...ratingValues) + 0.2 : 5;

        const yAxes: YAxisOptions[] = [
            {
                title: {
                    text: "Количество / длительность",
                },
            },
            {
                title: {
                    text: "Цена / прибыль",
                },
                opposite: true,
                labels: {
                    formatter() {
                        return `${compactNumberFormatter.format(Number(this.value))} ₸`;
                    },
                },
            },
            {
                min: placeAxisMin,
                max: placeAxisMax,
                reversed: true,
                visible: false,
                gridLineWidth: 0,
                title: {
                    text: undefined,
                },
            },
            {
                min: ratingAxisMin,
                max: ratingAxisMax,
                visible: false,
                gridLineWidth: 0,
                title: {
                    text: undefined,
                },
            },
        ];

        const series = seriesMetrics.map((metric) => {
            const config = METRIC_CONFIG[metric];
            const data = rows.map((row) => parseNumberLike(row[metric]));

            return {
                id: metric,
                type: config.type,
                name: config.label,
                color: config.color,
                data,
                yAxis: getSeriesAxisIndex(config.axis),
                zIndex: config.type === "column" ? 1 : 2,
                marker: config.type === "line" ? { enabled: false, radius: 3 } : undefined,
                tooltip: {
                    valueDecimals: config.valueType === "decimal" ? 2 : 0,
                },
            } as SeriesOptionsType;
        });

        return {
            chart: {
                height: DEFAULT_CHART_HEIGHT,
                scrollablePlotArea: {
                    minWidth: Math.max(720, rows.length * 92),
                },
            },
            credits: {
                enabled: false,
            },
            title: {
                text: undefined,
            },
            xAxis: {
                categories,
                crosshair: true,
                plotBands: buildPlotBands(rows),
            },
            yAxis: yAxes,
            legend: {
                enabled: true,
                align: "left",
                verticalAlign: "top",
            },
            tooltip: {
                shared: true,
                useHTML: true,
                formatter: function () {
                    const points = this.points ?? [];
                    const pointIndex = points[0]?.index ?? 0;
                    const row = rows[pointIndex];

                    let html = `<div style="min-width: 220px;">`;
                    html += `<div style="font-weight: 600; margin-bottom: 8px;">${categories[pointIndex] ?? ""}</div>`;

                    if (row?.product_out_of_stock) {
                        html += `<div style="margin-bottom: 4px; color: #b91c1c;">Нет в наличии</div>`;
                    }

                    if ((row?.product_discount ?? 0) > 0) {
                        html += `<div style="margin-bottom: 6px; color: #a16207;">Скидка: ${row?.product_discount}</div>`;
                    }

                    for (const point of points) {
                        const metric = point.series.userOptions.id as ChartMetricKey;
                        const color = typeof point.color === "string" ? point.color : "#475569";
                        html += `<div style="margin-top: 4px;"><span style="color: ${color};">●</span> ${point.series.name}: <b>${formatMetricValue(point.y as number | null, metric)}</b></div>`;
                    }

                    html += `</div>`;
                    return html;
                },
            },
            plotOptions: {
                series: {
                    animation: false,
                    states: {
                        inactive: {
                            opacity: 0.18,
                        },
                    },
                },
                column: {
                    borderRadius: 4,
                    pointPadding: 0.08,
                    groupPadding: 0.12,
                },
                line: {
                    lineWidth: 2.25,
                },
            },
            series,
        };
    }, [categories, rows, seriesMetrics]);

    const hasData = Boolean(chartOptions && seriesMetrics.length > 0);

    const handleDateChange = (value: null | [Dayjs | null, Dayjs | null]) => {
        if (!value || !value[0] || !value[1]) return;
        setDateRange([value[0], value[1]]);
    };

    return (
        <Card
            title="Dynamic of sales"
            variant="outlined"
            className="border border-slate-200 shadow-sm"
        >
            <Flex vertical gap={20} style={{ width: "100%" }}>
                <Alert
                    type="info"
                    showIcon
                    message="Красный фон показывает окна без наличия, жёлтый фон — окна со скидкой."
                />

                {isGranularityInvalid && (
                    <Alert
                        type="warning"
                        showIcon
                        message="Granularity должен быть alias или целым числом, например `half_month` или `7`."
                    />
                )}

                <div className="grid gap-4 xl:grid-cols-[minmax(280px,340px)_240px_minmax(180px,220px)]">
                    <div className="space-y-2">
                        <Text className="block text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
                            Период
                        </Text>
                        <RangePicker
                            value={dateRange}
                            onChange={handleDateChange}
                            allowClear={false}
                            format="YYYY-MM-DD"
                            size="middle"
                            className="w-full"
                        />
                    </div>

                    <div className="space-y-2">
                        <Text className="block text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
                            Granularity
                        </Text>
                        <AutoComplete
                            size="middle"
                            value={granularityInput}
                            options={GRANULARITY_INPUT_OPTIONS}
                            onChange={setGranularityInput}
                            allowClear
                            placeholder="half_month или 7"
                            status={isGranularityInvalid ? "warning" : undefined}
                            className="w-full"
                        />
                        <Text type="secondary" className="block text-xs">
                            Можно ввести alias (`quarter_month`, `half_month`, `month`) или свой
                            integer.
                        </Text>
                    </div>

                    <div className="space-y-2">
                        <Text className="block text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
                            City ID
                        </Text>
                        <Input
                            size="middle"
                            value={cityIdInput}
                            onChange={(event) => setCityIdInput(event.target.value)}
                            placeholder="Опционально"
                            allowClear
                        />
                    </div>
                </div>

                {!isGranularityInvalid && chartQuery.isLoading && (
                    <Skeleton active paragraph={{ rows: 10 }} />
                )}

                {!isGranularityInvalid && !chartQuery.isLoading && chartQuery.isError && (
                    <CustomResult
                        type="error"
                        title="Не удалось загрузить dynamic of sales"
                        axiosError={chartQuery.error}
                        withCard
                    />
                )}

                {!isGranularityInvalid &&
                    !chartQuery.isLoading &&
                    !chartQuery.isError &&
                    rows.length === 0 && (
                        <Empty
                            description="По выбранным фильтрам данные не найдены"
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                    )}

                {!isGranularityInvalid &&
                    !chartQuery.isLoading &&
                    !chartQuery.isError &&
                    rows.length > 0 &&
                    !hasData && (
                        <Empty
                            description="Ответ получен, но в нём нет числовых значений для построения графика"
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                    )}

                {!isGranularityInvalid &&
                    !chartQuery.isLoading &&
                    !chartQuery.isError &&
                    hasData &&
                    chartOptions && (
                        <Flex vertical gap={16}>
                            <div className="grid gap-3 md:grid-cols-3">
                                <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
                                    <Text type="secondary">Bucket-ов</Text>
                                    <Title level={4} style={{ margin: 0 }}>
                                        {compactNumberFormatter.format(rows.length)}
                                    </Title>
                                </div>
                                <div className="rounded-2xl border border-red-200 bg-red-50/70 px-4 py-3">
                                    <Text type="secondary">Нет в наличии</Text>
                                    <Title level={4} style={{ margin: 0 }}>
                                        {compactNumberFormatter.format(outOfStockCount)}
                                    </Title>
                                </div>
                                <div className="rounded-2xl border border-amber-200 bg-amber-50/70 px-4 py-3">
                                    <Text type="secondary">Окна со скидкой</Text>
                                    <Title level={4} style={{ margin: 0 }}>
                                        {compactNumberFormatter.format(discountCount)}
                                    </Title>
                                </div>
                            </div>

                            <Chart options={chartOptions} />
                        </Flex>
                    )}
            </Flex>
        </Card>
    );
}
